import { useState } from "react";
import { ListChecks, Plus, Trash2 } from "lucide-react";
import { saveCompanyTextList, type PrimeCompany, type CapabilityStatement, type TextListField } from "../api/CrmApi";
import { TEXT_LISTS } from "../textLists";
import { apiErrorMessage } from "../apiError";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "shared/tableStyles";

export const MAX_ENTRY_LENGTH = 4000;
export const MAX_ENTRIES = 20;

interface Props {
  company: PrimeCompany;
  field: TextListField;
  onClose: () => void;
  onSaved?: (company: PrimeCompany) => void;
  /** The list changed under the form (409): reload the company and close, so reopening starts from the current list. */
  onStale?: () => void;
}

/** The Edit past performance / project size / domestic performance form (one component, three lists): one text box per
 *  entry (several allowed), each with an optional label. Blank entries are dropped on save. */
export default function EditTextListModal({ company, field, onClose, onSaved, onStale }: Props) {
  const cfg = TEXT_LISTS[field];
  const stored = company[field] ?? [];
  const [items, setItems] = useState<CapabilityStatement[]>(stored.length > 0 ? stored : [{ text: "", label: "" }]);
  // The list the form was opened on, frozen: the save is refused if it changed since.
  const [base] = useState<CapabilityStatement[]>(stored);
  const [researchedNone, setResearchedNone] = useState(company[`${field}_basis`] === "reviewed_none");
  const [stale, setStale] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooLong = items.some((i) => i.text.length > MAX_ENTRY_LENGTH);
  const hasText = items.some((i) => i.text.trim() !== "");

  const update = (index: number, patch: Partial<CapabilityStatement>) =>
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  async function save() {
    setSaving(true);
    setError(null);
    setStale(false);
    try {
      const final = items.filter((i) => i.text.trim() !== "");
      const updated = await saveCompanyTextList(company.id, field, final, base, researchedNone && final.length === 0);
      onSaved?.(updated);
      onClose();
    } catch (err: any) {
      setStale(err?.response?.status === 409);
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      size="md"
      title={cfg.edit}
      description={company.name || company.domain || "company"}
      icon={<ListChecks />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving || tooLong}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-[11px] text-muted-foreground">
          {cfg.hint} This never rules a company out: a gap is a Hold or a condition.
        </p>
        {items.map((item, index) => (
          <div key={index} className="space-y-1.5 rounded-lg border border-border p-3" data-testid="text-list-item">
            <div className="flex items-center gap-2">
              <input
                value={item.label ?? ""}
                onChange={(e) => update(index, { label: e.target.value })}
                disabled={saving}
                maxLength={80}
                placeholder="Label (optional)"
                aria-label={`Label for entry ${index + 1}`}
                className={`flex-1 px-3 py-1.5 ${FORM_CONTROL}`}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                disabled={saving}
                aria-label={`Remove entry ${index + 1}`}
                title="Remove this entry"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            <textarea
              value={item.text}
              onChange={(e) => update(index, { text: e.target.value })}
              disabled={saving}
              rows={5}
              aria-label={`Entry ${index + 1}`}
              placeholder={cfg.placeholder}
              className={`w-full px-3 py-2 ${FORM_CONTROL} resize-y disabled:opacity-60`}
            />
            <p className={`text-[11px] text-right ${item.text.length > MAX_ENTRY_LENGTH ? "text-destructive" : "text-muted-foreground"}`}>
              {item.text.length} / {MAX_ENTRY_LENGTH}
            </p>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setItems((prev) => [...prev, { text: "", label: "" }])}
          disabled={saving || items.length >= MAX_ENTRIES}
        >
          <Plus className="w-3.5 h-3.5" /> Add entry
        </Button>
        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={researchedNone && !hasText}
            disabled={saving || hasText}
            onChange={(e) => setResearchedNone(e.target.checked)}
            className="accent-primary"
          />
          Researched — nothing found
        </label>
      </div>
      {error && (
        <Alert tone="danger" className="mt-4">
          {error}
          {stale && onStale && (
            <button type="button" onClick={onStale} className="ml-2 font-medium underline">
              Reload latest
            </button>
          )}
        </Alert>
      )}
    </Modal>
  );
}
